import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import BarChart from '../components/charts/BarChart';
import PieChart from '../components/charts/PieChart';
import LineChart from '../components/charts/LineChart';
import ScatterPlot from '../components/charts/ScatterPlot';
import LoadingIndicator from '../components/layout/LoadingIndicator';
import { shortenAgencyName } from './ElectorateDetails';

// Add helper function to get party color
const getPartyColor = (party) => {
  const colors = {
    'ALP': '#E53935', // Labor - red
    'LIB': '#0D47A1', // Liberal - blue
    'GRN': '#43A047', // Greens - green
    'NAT': '#33691E', // Nationals - dark green
    'PHON': '#FF6F00', // One Nation - orange
    'UAP': '#FFD600', // United Australia - yellow
    'CA': '#6A1B9A', // Centre Alliance - purple
    'KAP': '#BF360C', // Katter's Australian Party - dark orange
    'IND': '#607D8B'  // Independent - grey/blue
  };
  return colors[party] || '#607D8B'; // Default to grey if not found
};

// Add component for party badge
const PartyBadge = ({ party, margin }) => {
  const color = getPartyColor(party);
  return (
    <div className="flex items-center gap-2">
      <div 
        className="text-white px-2 py-1 rounded-full text-xs font-bold" 
        style={{ backgroundColor: color }}
      >
        {party}
      </div>
      <span className="text-xs">
        {margin ? `${margin}% margin` : ''}
      </span>
    </div>
  );
};

const ComparisonPage = ({ appData }) => {
  const { electorateNames } = useParams();
  const navigate = useNavigate();
  
  const [loading, setLoading] = useState(true);
  const [electorates, setElectorates] = useState([]);
  const [error, setError] = useState(null);
  const [yearlyComparisonData, setYearlyComparisonData] = useState([]);
  const [allAgenciesData, setAllAgenciesData] = useState({});
  const [activeTab, setActiveTab] = useState('grants');
  const [demographicsData, setDemographicsData] = useState([]); // Add state for demographics comparison

  // Parse electorate names from URL
  const names = useMemo(() => electorateNames ? electorateNames.split(',') : [], [electorateNames]);

  console.log('ComparisonPage: Names to load:', names);

  // Load electorate details
  useEffect(() => {
    let isMounted = true;
    
    const loadElectoratesData = async () => {
      try {
        setLoading(true);
        
        if (names.length === 0) {
          throw new Error('No electorates specified for comparison');
        }
        
        console.log("Loading data for electorates:", names);
        
        // Get the base URL for data files
        const baseUrl = process.env.PUBLIC_URL || '';
        
        // Load data for each electorate
        const loadedElectorates = [];
        const yearlyDataMap = {}; // To store yearly data for all electorates
        const agenciesDataMap = {}; // To store agency data for all electorates
        const demographicsInfo = []; // To store demographics data for all electorates
        
        for (const name of names) {
          try {
            // Format name for filename
            const filename = name.replace(/\s+/g, '_');
            
            // Fetch the electorate data
            const response = await fetch(`${baseUrl}/data/electorates/${filename}.json`);
            
            if (!response.ok) {
              throw new Error(`Failed to load data for ${name}: ${response.status}`);
            }
            
            // Parse the JSON data
            const electorateData = await response.json();
            
            // If grants property is missing, create an empty array
            if (!electorateData.grants) {
              electorateData.grants = [];
            }
            
            loadedElectorates.push(electorateData);
            
            // Extract yearly data for this electorate
            if (electorateData.byYear) {
              Object.entries(electorateData.byYear).forEach(([year, value]) => {
                if (!yearlyDataMap[year]) {
                  yearlyDataMap[year] = {};
                }
                yearlyDataMap[year][electorateData.name] = value;
              });
            }
            
            // Extract agency data for each electorate
            if (electorateData.byAgency) {
              agenciesDataMap[electorateData.name] = electorateData.byAgency;
            }
            
            // Extract demographics and political data
            demographicsInfo.push({
              name: electorateData.name,
              population: electorateData.population || 0,
              representative: electorateData.representative || 'Unknown',
              party: electorateData.party || 'Unknown',
              margin: electorateData.margin || 0,
              voters: electorateData.voters || 0,
              classification: electorateData.classification || 'Unknown',
              area: electorateData.area || 0,
              totalValue: electorateData.totalValue || 0,
              fundingPerPerson: electorateData.fundingPerPerson || 
                (electorateData.population && electorateData.totalValue ? 
                  electorateData.totalValue / electorateData.population : 0),
              fundingPerKm2: electorateData.fundingPerKm2 || 
                (electorateData.area && electorateData.totalValue ? 
                  electorateData.totalValue / electorateData.area : 0)
            });
            
          } catch (err) {
            console.error(`Error loading electorate data for ${name}:`, err);
            // Continue with the next electorate rather than failing the whole comparison
          }
        }
        
        if (isMounted) {
          setElectorates(loadedElectorates);
          setAllAgenciesData(agenciesDataMap);
          setDemographicsData(demographicsInfo);
          
          // Prepare line chart data for yearly comparison
          if (Object.keys(yearlyDataMap).length > 0) {
            setYearlyComparisonData(prepareLineChartData(yearlyDataMap, loadedElectorates));
          }
          
          setLoading(false);
        }
      } catch (err) {
        console.error('Error loading electorates:', err);
        if (isMounted) {
          setError('Failed to load electorate data: ' + err.message);
          setLoading(false);
        }
      }
    };
    
    loadElectoratesData();
    
    return () => {
      isMounted = false;
    };
  }, [names]);
  
  // Handle back button click
  const handleBack = () => {
    navigate('/');
  };

  // Get the top funding agencies across all electorates
  const prepareAgencyComparisonData = () => {
    // Skip if no data
    if (Object.keys(allAgenciesData).length === 0) {
      return [];
    }

    // Combine all agencies to find top ones across all electorates
    const allAgencies = {};
    
    // First, gather all unique agencies and their total value across all electorates
    Object.values(allAgenciesData).forEach(agencyData => {
      Object.entries(agencyData).forEach(([agency, value]) => {
        if (!allAgencies[agency]) {
          allAgencies[agency] = 0;
        }
        allAgencies[agency] += value;
      });
    });
    
    // Sort agencies by total value
    const sortedAgencies = Object.entries(allAgencies)
      .sort(([, a], [, b]) => b - a)
      .slice(0, 10) // Get top 10 agencies
      .map(([agency]) => agency);
    
    // Prepare comparison data
    const comparisonData = [];
    
    // For each top agency, get its value for each electorate
    sortedAgencies.forEach(agency => {
      const agencyData = {
        agency: shortenAgencyName(agency),
        fullName: agency // Store full name for tooltip
      };
      
      let totalValue = 0;
      
      // Add value for each electorate
      Object.entries(allAgenciesData).forEach(([electorate, agencies]) => {
        const value = agencies[agency] || 0;
        agencyData[electorate] = value;
        totalValue += value;
      });
      
      agencyData.total = totalValue; // Store total for sorting
      comparisonData.push(agencyData);
    });
    
    // Sort by total value
    return comparisonData.sort((a, b) => b.total - a.total);
  };

  const prepareLineChartData = (yearlyDataMap, electorates) => {
    if (!yearlyDataMap || Object.keys(yearlyDataMap).length === 0 || !electorates || electorates.length === 0) {
      return [];
    }

    // Get all available years
    const allYears = Object.keys(yearlyDataMap);
    
    // Create datasets for each electorate
    const datasets = electorates.map((electorate, index) => {
      // Color sequence for lines
      const colors = ['#3B82F6', '#EC4899', '#10B981', '#F59E0B', '#8B5CF6', '#EF4444'];
      const color = colors[index % colors.length];
      
      return {
        label: electorate.name,
        data: allYears.map(year => {
          // Find value for this electorate and year
          const yearData = yearlyDataMap[year];
          if (!yearData || !yearData[electorate.name]) {
            return 0; // No data for this year/electorate
          }
          return yearData[electorate.name];
        }),
        borderColor: color,
        backgroundColor: color.replace(')', ', 0.1)').replace('rgb', 'rgba'),
        borderWidth: 2,
        tension: 0.1
      };
    });

    return {
      labels: allYears.sort(),
      datasets
    };
  };

  const getElectoratePieData = (electorateName) => {
    // ... existing code ...
  };

  // Create function to prepare demographic comparison data
  const prepareDemographicsComparisonData = () => {
    // Skip if no data
    if (demographicsData.length === 0) {
      return [];
    }

    return demographicsData;
  };

  // Add a new function to prepare party funding data
  const preparePartyFundingData = () => {
    if (electorates.length === 0) return [];
    
    // Group electorates by party
    const partyGroups = {};
    
    electorates.forEach(electorate => {
      if (!electorate.party) return;
      
      if (!partyGroups[electorate.party]) {
        partyGroups[electorate.party] = {
          party: electorate.party,
          totalFunding: 0,
          electorateCount: 0,
          totalPopulation: 0,
          averageFundingPerPerson: 0,
          color: getPartyColor(electorate.party)
        };
      }
      
      partyGroups[electorate.party].totalFunding += electorate.totalValue || 0;
      partyGroups[electorate.party].electorateCount += 1;
      partyGroups[electorate.party].totalPopulation += electorate.population || 0;
    });
    
    // Calculate averages
    Object.values(partyGroups).forEach(group => {
      if (group.totalPopulation > 0) {
        group.averageFundingPerPerson = group.totalFunding / group.totalPopulation;
      }
      
      if (group.electorateCount > 0) {
        group.averageFundingPerElectorate = group.totalFunding / group.electorateCount;
      }
    });
    
    return Object.values(partyGroups).sort((a, b) => b.totalFunding - a.totalFunding);
  };

  // Add a new function to prepare margin vs funding data
  const prepareMarginFundingData = () => {
    if (electorates.length < 2) return [];
    
    return electorates
      .filter(e => e.margin !== undefined && e.totalValue && e.population)
      .map(e => ({
        x: parseFloat(e.margin),
        y: e.totalValue / e.population,
        label: e.name,
        color: getPartyColor(e.party)
      }));
  };

  // Determine which tab is active
  const handleTabChange = (tab) => {
    setActiveTab(tab);
  };

  // Render loading state or error message if needed
  if (loading) {
    return (
      <div className="container mx-auto p-4">
        <h1 className="text-2xl font-bold mb-4">Compare Electorates</h1>
        <div className="text-center py-10"><LoadingIndicator />Loading electorate data...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="container mx-auto p-4">
        <h1 className="text-2xl font-bold mb-4">Compare Electorates</h1>
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded">
          {error}
        </div>
        <button 
          onClick={handleBack}
          className="mt-4 bg-blue-500 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded"
        >
          Back to Home
        </button>
      </div>
    );
  }

  if (electorates.length === 0) {
    return (
      <div className="container mx-auto p-4">
        <h1 className="text-2xl font-bold mb-4">Compare Electorates</h1>
        <div className="bg-yellow-100 border border-yellow-400 text-yellow-700 px-4 py-3 rounded">
          No electorate data found to compare.
        </div>
        <button 
          onClick={handleBack}
          className="mt-4 bg-blue-500 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded"
        >
          Back to Home
        </button>
      </div>
    );
  }

  // Format currency with commas and dollar sign
  const formatCurrency = (value) => {
    return value.toLocaleString('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    });
  };

  // Get the comparison data
  const agencyComparisonData = prepareAgencyComparisonData();
  const demographicsComparisonData = prepareDemographicsComparisonData();
  const partyFundingData = preparePartyFundingData();
  const marginFundingData = prepareMarginFundingData();

  return (
    <div className="container mx-auto p-4">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold">Compare Electorates</h1>
        <button 
          onClick={handleBack}
          className="bg-blue-500 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded"
        >
          Back to Home
        </button>
      </div>
      
      <div className="mb-6">
        <div className="bg-white p-4 rounded-lg shadow mb-4">
          <h2 className="text-xl font-semibold mb-2">Electorates Being Compared</h2>
          <ul className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
            {electorates.map(electorate => (
              <li key={electorate.name} className="bg-gray-50 p-3 rounded">
                <div className="flex justify-between items-start">
                  <div>
                    <h3 className="font-medium text-lg">{electorate.name}</h3>
                    <p className="text-sm text-gray-600">{electorate.state}</p>
                    {electorate.party && (
                      <div className="mt-1">
                        <PartyBadge party={electorate.party} margin={electorate.margin} />
                      </div>
                    )}
                  </div>
                  <div className="text-right">
                    <div className="font-bold text-green-600">
                      {formatCurrency(electorate.totalValue)}
                    </div>
                    <div className="text-xs text-gray-500">Total funding</div>
                  </div>
                </div>
              </li>
            ))}
          </ul>
        </div>
      </div>
      
      {/* Tabs Navigation */}
      <div className="border-b border-gray-200 mb-6">
        <ul className="flex flex-wrap -mb-px text-sm font-medium text-center">
          <li className="mr-2">
            <button
              className={`inline-block p-4 rounded-t-lg ${activeTab === 'grants' ? 'border-b-2 border-blue-600 text-blue-600' : 'border-b-2 border-transparent hover:text-gray-600 hover:border-gray-300'}`}
              onClick={() => handleTabChange('grants')}
            >
              Grant Funding
            </button>
          </li>
          <li className="mr-2">
            <button
              className={`inline-block p-4 rounded-t-lg ${activeTab === 'agencies' ? 'border-b-2 border-blue-600 text-blue-600' : 'border-b-2 border-transparent hover:text-gray-600 hover:border-gray-300'}`}
              onClick={() => handleTabChange('agencies')}
            >
              Agency Funding
            </button>
          </li>
          <li className="mr-2">
            <button
              className={`inline-block p-4 rounded-t-lg ${activeTab === 'demographics' ? 'border-b-2 border-blue-600 text-blue-600' : 'border-b-2 border-transparent hover:text-gray-600 hover:border-gray-300'}`}
              onClick={() => handleTabChange('demographics')}
            >
              Demographics & Politics
            </button>
          </li>
        </ul>
      </div>
      
      {/* Grant Funding Tab Content */}
      {activeTab === 'grants' && (
        <div>
          <div className="bg-white p-6 rounded-lg shadow-md mb-8">
            <h2 className="text-xl font-bold mb-4">Total Funding Comparison</h2>
            <div className="h-48">
              <BarChart 
                data={electorates.map(e => ({
                  name: e.name,
                  value: e.totalValue,
                  formattedValue: formatCurrency(e.totalValue)
                }))} 
                compact={true}
                dataLabel="Total Funding"
              />
            </div>
          </div>
          
          <div className="bg-white p-6 rounded-lg shadow-md mb-8">
            <h2 className="text-xl font-bold mb-4">Yearly Funding Trends</h2>
            <div className="h-80">
              {yearlyComparisonData && yearlyComparisonData.length > 0 ? (
                <LineChart data={yearlyComparisonData} />
              ) : (
                <div className="flex items-center justify-center h-full">
                  <p className="text-gray-500">No yearly data available</p>
                </div>
              )}
            </div>
          </div>
          
          <div className="bg-white p-6 rounded-lg shadow-md">
            <h2 className="text-xl font-bold mb-4">Funding per Metric Comparison</h2>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Electorate
                    </th>
                    <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Total Funding
                    </th>
                    <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Funding per Person
                    </th>
                    <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Funding per km²
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {electorates.map((electorate) => (
                    <tr key={electorate.name} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                        {electorate.name}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-right text-gray-900">
                        {formatCurrency(electorate.totalValue)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-right text-gray-900">
                        {electorate.population ? 
                          formatCurrency(electorate.totalValue / electorate.population) : 'N/A'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-right text-gray-900">
                        {electorate.area ? 
                          formatCurrency(electorate.totalValue / electorate.area) : 'N/A'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}
      
      {/* Agency Funding Tab Content */}
      {activeTab === 'agencies' && (
        <div>
          {electorates.length >= 2 ? (
            <>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
                {electorates.slice(0, 2).map(electorate => (
                  <div key={electorate.name} className="bg-white p-6 rounded-lg shadow-md">
                    <h2 className="text-xl font-bold mb-2 text-center">{electorate.name}</h2>
                    <p className="text-center text-sm text-gray-500 mb-4">
                      Total Funding: {formatCurrency(electorate.totalValue)}
                    </p>
                    <div className="h-80">
                      {electorate.byAgency && Object.keys(electorate.byAgency).length > 0 ? (
                        <PieChart data={getElectoratePieData(electorate.name)} />
                      ) : (
                        <div className="flex items-center justify-center h-full">
                          <p className="text-gray-500">No agency data available</p>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>

              <div className="bg-white p-6 rounded-lg shadow-md mb-8">
                <h2 className="text-xl font-bold mb-4">Top Funding Agencies Comparison</h2>
                <p className="text-sm text-gray-500 mb-4">
                  The table below shows the top funding agencies across all compared electorates.
                </p>
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Agency
                        </th>
                        {electorates.map(electorate => (
                          <th key={electorate.name} scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                            {electorate.name}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {agencyComparisonData.map((agency) => (
                        <tr key={agency.agency} className="hover:bg-gray-50">
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900" title={agency.fullName}>
                            {agency.agency}
                          </td>
                          {electorates.map(electorate => (
                            <td key={`${agency.agency}-${electorate.name}`} className="px-6 py-4 whitespace-nowrap text-sm text-right text-gray-900">
                              {agency[electorate.name] ? formatCurrency(agency[electorate.name]) : '-'}
                            </td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </>
          ) : (
            <div className="bg-white p-6 rounded-lg shadow-md mb-8 text-center">
              <h2 className="text-xl font-bold mb-4">Agency Comparison</h2>
              <p className="text-gray-500 mb-4">
                Please select at least two electorates to compare their agency funding.
              </p>
              <button
                onClick={handleBack}
                className="bg-blue-500 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded"
              >
                Back to Selection
              </button>
            </div>
          )}
        </div>
      )}
      
      {/* Demographics & Politics Tab Content */}
      {activeTab === 'demographics' && (
        <div>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
            <div className="bg-white p-6 rounded-lg shadow-md">
              <h2 className="text-xl font-bold mb-4">Funding per Resident</h2>
              <div className="h-80">
                <BarChart 
                  data={demographicsComparisonData.map(d => ({
                    name: d.name,
                    value: d.fundingPerPerson,
                    formattedValue: `$${Math.round(d.fundingPerPerson).toLocaleString()}`
                  }))}
                  compact={electorates.length <= 3}
                  color="#10B981"
                  dataLabel="Funding per Resident"
                />
              </div>
            </div>
            
            <div className="bg-white p-6 rounded-lg shadow-md">
              <h2 className="text-xl font-bold mb-4">Electoral Margin</h2>
              <div className="h-80">
                <BarChart 
                  data={demographicsComparisonData.map(d => ({
                    name: d.name,
                    value: d.margin,
                    formattedValue: `${d.margin.toFixed(1)}%`,
                    color: getPartyColor(d.party)
                  }))}
                  compact={electorates.length <= 3}
                  useCustomColors={true}
                  dataLabel="Electoral Margin (%)"
                />
              </div>
            </div>
          </div>
          
          <div className="bg-white p-6 rounded-lg shadow-md mb-8">
            <h2 className="text-xl font-bold mb-4">Electorate Comparison</h2>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Electorate
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Party
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Classification
                    </th>
                    <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Total Funding
                    </th>
                    <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Per Resident
                    </th>
                    <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Population
                    </th>
                    <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Area (km²)
                    </th>
                    <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Pop. Density
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {demographicsComparisonData.map((d) => (
                    <tr key={d.name} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                        {d.name}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <PartyBadge party={d.party} margin={d.margin} />
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {d.classification}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-right text-gray-900">
                        {formatCurrency(d.totalValue)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-right text-gray-900">
                        {d.population ? formatCurrency(d.fundingPerPerson) : 'N/A'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-right text-gray-900">
                        {d.population.toLocaleString()}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-right text-gray-900">
                        {d.area.toLocaleString()}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-right text-gray-900">
                        {d.population && d.area ? 
                          `${Math.round(d.population / d.area).toLocaleString()}/km²` : 'N/A'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
          
          <div className="bg-white p-6 rounded-lg shadow-md">
            <h2 className="text-xl font-bold mb-4">Funding Analysis by Demographics</h2>
            <p className="mb-4">
              This analysis reveals how government funding varies across different regions and
              political alignments. Key insights:
            </p>
            <ul className="list-disc pl-5 mb-4 space-y-2">
              <li>
                <strong>Party Disparities:</strong> Compare how funding varies between electorates held
                by different political parties.
              </li>
              <li>
                <strong>Urban vs. Rural:</strong> See differences in funding between metropolitan, provincial, 
                and rural electorates.
              </li>
              <li>
                <strong>Population Factors:</strong> Examine if more populated electorates receive more 
                funding per capita than less populated ones.
              </li>
            </ul>
          </div>
        </div>
      )}
    </div>
  );
};

export default ComparisonPage;
